import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Dayjs } from 'dayjs';
import { IAppointment } from '../../../core/models/appointment.model';
import { EAppointmentStatus } from '../../../core/enums/appointment-status.enum';
import { isSameDay, tz } from '../../../core/helpers/calendar.helper';

interface IMonthDay {
  date: Dayjs;
  isToday: boolean;
  isCurrentMonth: boolean;
  appointments: IAppointment[];
}

@Component({
  selector: 'coach-agenda-month',
  templateUrl: './agenda-month.component.html',
  styleUrl: './agenda-month.component.scss',
})
export class AgendaMonthComponent implements OnChanges {
  @Input() monthStart!: Dayjs;
  @Input() appointments: IAppointment[] = [];
  @Output() daySelected = new EventEmitter<Dayjs>();

  weeks: IMonthDay[][] = [];

  ngOnChanges(): void {
    this.buildGrid();
  }

  levelKey(appointment: IAppointment): string {
    return appointment.student?.userInfo?.level?.key ?? 'default';
  }

  initials(name?: string | null): string {
    if (!name) {
      return '?';
    }

    return name.trim().split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join('').toLocaleUpperCase('tr-TR');
  }

  private buildGrid() {
    if (!this.monthStart) {
      return;
    }

    const today = tz();
    const active = this.appointments.filter(a => a.status !== EAppointmentStatus.Cancelled);

    // The grid always starts on a Monday and covers whole weeks, so neighbouring-month days fill
    // the edges instead of leaving holes.
    const gridStart = this.monthStart.startOf('month').startOf('isoWeek');
    const gridEnd = this.monthStart.endOf('month').endOf('isoWeek');
    const weekCount = Math.ceil(gridEnd.diff(gridStart, 'day') / 7);

    this.weeks = Array.from({length: weekCount}, (_, weekIndex) =>
      Array.from({length: 7}, (__, dayIndex) => {
        const date = gridStart.add((weekIndex * 7) + dayIndex, 'day');

        return {
          date,
          isToday: isSameDay(date, today),
          isCurrentMonth: date.month() === this.monthStart.month(),
          appointments: active
            .filter(a => isSameDay(tz(a.startsAt), date))
            .sort((a, b) => tz(a.startsAt).valueOf() - tz(b.startsAt).valueOf()),
        };
      })
    );
  }
}
